import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, HomeIcon, SearchIcon, ChevronRightIcon, LockIcon, ShieldCheckIcon, CheckIcon, HeartHandshakeIcon, ReceiptIcon, BuildingIcon, LandmarkIcon, FileTextIcon, UsersIcon, MapPinIcon, NavigationIcon, HandHeartIcon, DownloadIcon, Share2Icon, StoreIcon, ClockIcon, CalendarClockIcon, PlusIcon, XIcon, Edit3Icon, Trash2Icon, AlertTriangleIcon } from 'lucide-react';
import { useTheme } from '../utils/theme';
import { HomeIconButton, TransactionReceipt } from './TransactionReceipt';
// ── Shared CTA button style (canonical, matches SendMoneyScreen) ────────────
// height: 55px | width: 280px | border-radius: 8px | color: #F37024
const ctaStyle: React.CSSProperties = {
  height: '55px',
  width: '280px',
  borderRadius: '8px',
  background: '#F37024',
  color: '#FFFFFF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 600,
  fontSize: '16px',
  border: 'none',
  cursor: 'pointer',
  transition: 'opacity 0.15s',
  margin: '0 auto'
};
interface ZakaatScreenProps {
  onBack?: () => void;
  onNavigateHome?: () => void;
  onNavigateToMerchantRequests?: () => void;
  onNavigateToMerchantHistory?: () => void;
}
type Step = 'services' | 'orgs' | 'review' | 'otp' | 'success' | 'merchant_menu' | 'scheduled_list' | 'scheduled_new' | 'scheduled_review' | 'scheduled_mpin' | 'scheduled_success' | 'scheduled_detail';
type OrgTab = 'zakat' | 'sadqaat';
interface Org {
  id: string;
  name: string;
  account: string;
  initials: string;
  color: string;
}
const MORE_SERVICES = [{
  id: 'zakat',
  label: 'Zakat / Donations',
  desc: 'Pay Zakat, Fitra and Sadqaat securely',
  icon: HandHeartIcon,
  highlight: true
}, {
  id: 'merchant',
  label: 'Merchant Payment',
  desc: 'Payment requests and P2M history',
  icon: StoreIcon,
  highlight: false
}, {
  id: 'scheduled',
  label: 'Scheduled Payments',
  desc: 'Manage recurring and future payments',
  icon: CalendarClockIcon,
  highlight: false
}];
export interface ScheduledPayment {
  id: string;
  payTo: string;
  payToAccount: string;
  payFrom: string;
  payFromAccount: string;
  amount: number;
  type: string;
  frequency: string;
  startDate: string;
  endDate?: string;
  time: string;
  status: 'Active' | 'Completed' | 'Failed';
  createdOn: string;
  comments?: string;
}
const MOCK_SCHEDULED: ScheduledPayment[] = [{
  id: 'sp1',
  payTo: 'Al-khidmat Foundation',
  payToAccount: '1234 **** **** 6544',
  payFrom: 'Kashish Shewani',
  payFromAccount: '1234 **** **** 6544',
  amount: 5000,
  type: 'Zakat',
  frequency: 'Monthly',
  startDate: '20/12/2026',
  endDate: '20/12/2027',
  time: '09:45 AM',
  status: 'Active',
  createdOn: '15/11/2026'
}, {
  id: 'sp2',
  payTo: 'Storm Fiber',
  payToAccount: '03223556978',
  payFrom: 'Kashish Shewani',
  payFromAccount: '1234 **** **** 6544',
  amount: 3448,
  type: 'Internet Bill',
  frequency: 'Once',
  startDate: '25/12/2026',
  time: '12:00 PM',
  status: 'Failed',
  createdOn: '10/12/2026'
}];
const ZAKAT_ORGS: Org[] = [{
  id: 'edhi',
  name: 'Edhi Foundation',
  account: 'PK24BOPM0001234567',
  initials: 'EF',
  color: '#1E7A46'
}, {
  id: 'skmt',
  name: 'Shaukat Khanum Memorial',
  account: 'PK11BOPM0009988776',
  initials: 'SK',
  color: '#C0392B'
}, {
  id: 'saylani',
  name: 'Saylani Welfare Trust',
  account: 'PK56BOPM0004455667',
  initials: 'SW',
  color: '#2E86C1'
}, {
  id: 'alkhidmat',
  name: 'Al Khidmat Foundation',
  account: 'PK78BOPM0007788991',
  initials: 'AK',
  color: '#117864'
}, {
  id: 'akhuwat',
  name: 'Akhuwat Foundation',
  account: 'PK33BOPM0002211009',
  initials: 'AF',
  color: '#7D3C98'
}];
const SADQAAT_ORGS: Org[] = [{
  id: 'jdc',
  name: 'JDC Foundation Pakistan',
  account: 'PK90BOPM0006677889',
  initials: 'JD',
  color: '#D68910'
}, {
  id: 'chhipa',
  name: 'Chhipa Welfare Association',
  account: 'PK45BOPM0003344556',
  initials: 'CW',
  color: '#CB4335'
}, {
  id: 'indus',
  name: 'Indus Hospital & Health Network',
  account: 'PK67BOPM0005566778',
  initials: 'IH',
  color: '#1F618D'
}, {
  id: 'transparent',
  name: 'Transparent Hands',
  account: 'PK12BOPM0008899001',
  initials: 'TH',
  color: '#148F77'
}];
const CUSTOMER = {
  name: 'KASHISH SOHAIL SHEWANI',
  account: '1234-5678-9012',
  balance: 250000
};
const slideVariants = {
  enter: (dir: number) => ({
    x: dir > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: 'easeOut'
    }
  },
  exit: (dir: number) => ({
    x: dir < 0 ? '100%' : '-100%',
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: 'easeIn'
    }
  })
};
const formatPKR = (n: number) => n.toLocaleString('en-US', {
  minimumFractionDigits: 0
});
export function ZakaatScreen({
  onBack,
  onNavigateHome,
  onNavigateToMerchantRequests,
  onNavigateToMerchantHistory
}: ZakaatScreenProps) {
  const {
    isDark,
    isPlatinum
  } = useTheme();
  const [step, setStep] = useState<Step>('services');
  const [direction, setDirection] = useState(1);
  const [tab, setTab] = useState<OrgTab>('zakat');
  const [search, setSearch] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<Org | null>(null);
  const [amount, setAmount] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [resendTimer, setResendTimer] = useState(15);
  // Scheduled Payments State
  const [scheduledPayments, setScheduledPayments] = useState<ScheduledPayment[]>(MOCK_SCHEDULED);
  const [schedSearch, setSchedSearch] = useState('');
  const [schedTab, setSchedTab] = useState<'once' | 'recurring'>('once');
  const [schedDate, setSchedDate] = useState('');
  const [schedEndDate, setSchedEndDate] = useState('');
  const [schedTime, setSchedTime] = useState('12:00 AM');
  const [schedTerms, setSchedTerms] = useState(false);
  const [schedAmount, setSchedAmount] = useState('');
  const [schedPayTo, setSchedPayTo] = useState('');
  const [schedPayFrom, setSchedPayFrom] = useState('Kashish Shewani / 1234 **** **** 6544');
  const [schedType, setSchedType] = useState('');
  const [schedFreq, setSchedFreq] = useState('Monthly');
  const [schedCustomDays, setSchedCustomDays] = useState('');
  const [schedComments, setSchedComments] = useState('');
  const [selectedSched, setSelectedSched] = useState<ScheduledPayment | null>(null);
  const [schedMpin, setSchedMpin] = useState(['', '', '', '']);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isEditingSched, setIsEditingSched] = useState(false);
  const navigateTo = (next: Step, dir = 1) => {
    setDirection(dir);
    setStep(next);
  };
  // ---- Theme palette ----
  const isLight = !isDark && !isPlatinum;
  const textPri = isLight ? 'text-[#1A1A1A]' : 'text-white';
  const textSec = isLight ? 'text-[#6B6B6B]' : isPlatinum ? 'text-[#A0A0A5]' : 'text-[#B8A89E]';
  const pageBg = isLight ? 'bg-[#FFF5F0]' : isPlatinum ? 'bg-[#1C1C1E]' : 'bg-[#1A0E08]';
  const cardSurface = isLight ? 'bg-white border border-[#F0E8E3]' : isPlatinum ? 'bg-[#2C2C2E] border border-[rgba(160,160,165,0.12)]' : 'bg-[#2D1810] border border-[rgba(243,112,36,0.12)]';
  const maroon = isLight ? '#5A1408' : '#F37024';
  const donationType = tab === 'zakat' ? 'Zakat / Fitra' : 'Sadqaat';
  // OTP resend timer
  useEffect(() => {
    if (step !== 'otp') return;
    setResendTimer(15);
    setOtp(['', '', '', '', '', '']);
  }, [step]);
  useEffect(() => {
    if (step !== 'otp' || resendTimer <= 0) return;
    const id = setInterval(() => setResendTimer(t => t > 0 ? t - 1 : 0), 1000);
    return () => clearInterval(id);
  }, [step, resendTimer]);
  // Amount validation
  const amountNum = Number(amount);
  const amountValid = amount !== '' && amountNum > 0 && amountNum <= CUSTOMER.balance;
  const amountError = amount !== '' && (amountNum <= 0 ? 'Enter an amount greater than zero' : amountNum > CUSTOMER.balance ? 'Amount exceeds available balance' : '');
  const otpComplete = otp.every(d => d !== '');
  const handleOtpChange = (index: number, value: string) => {
    // Paste support
    if (value.length > 1) {
      const digits = value.replace(/[^\d]/g, '').slice(0, 6).split('');
      const next = ['', '', '', '', '', ''];
      digits.forEach((d, i) => next[i] = d);
      setOtp(next);
      const lastIdx = Math.min(digits.length, 6) - 1;
      if (lastIdx >= 0) document.getElementById(`zakat-otp-${Math.min(lastIdx + 1, 5)}`)?.focus();
      return;
    }
    const digit = value.replace(/[^\d]/g, '').slice(-1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);
    if (digit && index < 5) {
      document.getElementById(`zakat-otp-${index + 1}`)?.focus();
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`zakat-otp-${index - 1}`)?.focus();
    }
  };
  const txnId = 'TXN20260012345';
  const refNumber = 'DON000987654';
  const now = new Date();
  const txnDateTime = now.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  // Reusable header
  const Header = ({
    title,
    onBackClick
  }: {
    title: string;
    onBackClick: () => void;
  }) => <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-3768" data-mp="3lyru8ei8162e">
      <button onClick={onBackClick} aria-label="Back" className={`p-2 -ml-2 ${textPri}`} data-id="element-3769" data-mp="3pz2coqn79oy5">
        <ArrowLeftIcon className="w-6 h-6" strokeWidth={2} data-id="element-3770" data-mp="1ung99h1xxqwr" />
      </button>
      <h1 className={`text-[18px] font-semibold ${textPri}`} style={{
      fontFamily: 'Poppins, system-ui, sans-serif'
    }} data-id="element-3771" data-mp="1zmj6ifboisu6">
        {title}
      </h1>
      <HomeIconButton onNavigateHome={onNavigateHome} data-id="element-3772" data-mp="1pomw5pqkg1x4" />
    </div>;
  // ===== Screen 1: More Services =====
  const renderServices = () => <motion.div key="services" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-10 ${pageBg}`} style={{
    fontFamily: 'Poppins, system-ui, sans-serif'
  }} data-id="element-3773" data-mp="2df8ugparq7tx">
      <Header title="More Services" onBackClick={onBack ?? (() => {})} data-id="element-3774" data-mp="28mfo5wznirfq" />
      <div className="px-6 mt-2" data-id="element-3775" data-mp="2wdbmlcym4kvy">
        <p className={`text-[13px] mb-5 ${textSec}`} data-id="element-3776" data-mp="2wc9zub88i9ye">
          Additional banking services at your fingertips.
        </p>
        <div className="space-y-3" data-id="element-3777" data-mp="31537b6v37we6">
          {MORE_SERVICES.map((s, idx) => {
          const Icon = s.icon;
          const isZakat = s.id === 'zakat';
          return <motion.button key={s.id} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: idx * 0.04
          }} whileTap={{
            scale: 0.98
          }} onClick={() => {
            if (isZakat) navigateTo('orgs');else if (s.id === 'merchant') navigateTo('merchant_menu');else if (s.id === 'scheduled') navigateTo('scheduled_list');
          }} className={`w-full p-4 rounded-lg flex items-center gap-4 text-left transition-colors ${isZakat ? isLight ? 'bg-[#F37024]/8 border-2 border-[#F37024]' : 'bg-[#F37024]/12 border-2 border-[#F37024]' : cardSurface}`} data-id="element-3778" data-mp="kp6tnywyl7nh" data-mpi={idx}>
                <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0" style={{
              background: isZakat ? '#F37024' : isLight ? '#FBE4D3' : 'rgba(243,112,36,0.14)'
            }} data-id="element-3779" data-mp="1z5ym0lz21a99">
                  <Icon className="w-5 h-5" strokeWidth={2} style={{
                color: isZakat ? '#FFFFFF' : '#F37024'
              }} data-id="element-3780" data-mp="1bf2n74w6lesi" />
                </div>
                <div className="min-w-0 flex-1" data-id="element-3781" data-mp="1q6lazova8dmf">
                  <p className={`text-[14px] font-semibold truncate ${textPri}`} data-id="element-3782" data-mp="sqfjappqqvgi">
                    {s.label}
                  </p>
                  <p className={`text-[12px] truncate ${textSec}`} data-id="element-3783" data-mp="xno4z0a3lhmi">{s.desc}</p>
                </div>
                <ChevronRightIcon className={`w-5 h-5 shrink-0 ${textSec}`} strokeWidth={2} data-id="element-3784" data-mp="3ayz7j0cn3tfv" />
              </motion.button>;
        })}
        </div>
      </div>
    </motion.div>;
  // ===== Screen 2: Org List =====
  const orgs = tab === 'zakat' ? ZAKAT_ORGS : SADQAAT_ORGS;
  const filteredOrgs = orgs.filter(o => o.name.toLowerCase().includes(search.toLowerCase()));
  const renderOrgs = () => <motion.div key="orgs" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-10 ${pageBg}`} style={{
    fontFamily: 'Poppins, system-ui, sans-serif'
  }} data-id="element-3785" data-mp="2n94uoisbm4vm">
      <Header title="Zakat / Fitra & Sadqaat" onBackClick={() => navigateTo('services', -1)} data-id="element-3786" data-mp="299dbm3my5yar" />

      {/* Tabs */}
      <div className="px-6 mt-2" data-id="element-3787" data-mp="24deu9001dgju">
        <div className={`relative flex rounded-lg p-1 ${isLight ? 'bg-[#F5EBE6]' : 'bg-[#2D1810]'}`} data-id="element-3788" data-mp="88gxq9hyoo3x">
          <motion.div layout className="absolute top-1 bottom-1 rounded-lg bg-[#F37024] shadow-md" initial={false} animate={{
          left: tab === 'zakat' ? 4 : '50%',
          right: tab === 'zakat' ? '50%' : 4
        }} transition={{
          type: 'spring',
          damping: 28,
          stiffness: 280
        }} data-id="element-3789" data-mp="d7jt12853fyp" />
          <button onClick={() => {
          setTab('zakat');
          setSearch('');
        }} className={`relative z-10 flex-1 py-2.5 text-[13px] font-semibold rounded-lg transition-colors ${tab === 'zakat' ? 'text-white' : textSec}`} data-id="element-3790" data-mp="3qnrdamx1hf7z">
            Zakat / Fitra
          </button>
          <button onClick={() => {
          setTab('sadqaat');
          setSearch('');
        }} className={`relative z-10 flex-1 py-2.5 text-[13px] font-semibold rounded-lg transition-colors ${tab === 'sadqaat' ? 'text-white' : textSec}`} data-id="element-3791" data-mp="19vpx9o15g6cg">
            Sadqaat
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="px-6 mt-5" data-id="element-3792" data-mp="395sod7k4085u">
        <div className={`flex items-center gap-3 px-4 rounded-lg h-[52px] ${cardSurface}`} data-id="element-3793" data-mp="34e16dlf0rarb">
          <SearchIcon className={`w-5 h-5 shrink-0 ${textSec}`} strokeWidth={2} data-id="element-3794" data-mp="3i4mqfdg7lqqs" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search charitable organization" className={`w-full bg-transparent outline-none text-[14px] ${textPri} placeholder:opacity-50`} data-id="element-3795" data-mp="3e2j1pn4j4pnk" />
        </div>
      </div>

      {/* Org list */}
      <div className="px-6 mt-5 space-y-3" data-id="element-3796" data-mp="1xpuifck8a28u">
        {filteredOrgs.length === 0 && <p className={`text-center text-[13px] mt-8 ${textSec}`} data-id="element-3797" data-mp="1srj9d41rbvro">
            No organizations found.
          </p>}
        {filteredOrgs.map((org, idx) => <motion.button key={org.id} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.25,
        delay: idx * 0.04
      }} whileTap={{
        scale: 0.98
      }} onClick={() => {
        setSelectedOrg(org);
        setAmount('');
        navigateTo('review');
      }} className={`w-full p-4 rounded-lg flex items-center gap-4 text-left ${cardSurface}`} data-id="element-3798" data-mp="1lo6z2y4jp4y" data-mpi={idx}>
            <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 text-white text-[14px] font-bold" style={{
          background: org.color
        }} data-id="element-3799" data-mp="tcubrwosib0k">
              {org.initials}
            </div>
            <div className="min-w-0 flex-1" data-id="element-3800" data-mp="1dghz4wmbosjk">
              <p className={`text-[14px] font-semibold truncate ${textPri}`} data-id="element-3801" data-mp="1dd3zvx8ygzgc">
                {org.name}
              </p>
              <p className={`text-[12px] truncate ${textSec}`} data-id="element-3802" data-mp="13h10v09ehgxm">{org.account}</p>
            </div>
            <ChevronRightIcon className={`w-5 h-5 shrink-0 ${textSec}`} strokeWidth={2} data-id="element-3803" data-mp="zfywy64jggpb" />
          </motion.button>)}
      </div>
    </motion.div>;
  // ===== Screen 3: Review =====
  const serviceCharge = 0;
  const totalAmount = amountNum + serviceCharge;
  const renderReview = () => <motion.div key="review" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${pageBg}`} style={{
    fontFamily: 'Poppins, system-ui, sans-serif'
  }} data-id="element-3804" data-mp="1meu0ntoq9aqs">
      <Header title="Review" onBackClick={() => navigateTo('orgs', -1)} data-id="element-3805" data-mp="2a468ap4epaqz" />
      <div className="px-6 mt-2 flex-1 overflow-y-auto" data-id="element-3806" data-mp="2tthtvesmstib">
        {/* Customer account */}
        <div className={`rounded-lg p-5 mb-4 ${cardSurface}`} data-id="element-3807" data-mp="2ykzug1qqss2r">
          <p className={`text-[11px] font-semibold uppercase tracking-wider mb-3 ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-3808" data-mp="2ymb24cnb6jz5">
            From Account
          </p>
          <p className={`text-[15px] font-semibold ${textPri}`} data-id="element-3809" data-mp="2oskc33bja1hc">
            {CUSTOMER.name}
          </p>
          <p className={`text-[13px] mt-1 ${textSec}`} data-id="element-3810" data-mp="3ciet7x2vyc7e">{CUSTOMER.account}</p>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-black/5" data-id="element-3811" data-mp="3hikmfhot06rl">
            <span className={`text-[12px] ${textSec}`} data-id="element-3812" data-mp="32ltlv53e8p0e">Available Balance</span>
            <span className={`text-[14px] font-bold ${textPri}`} data-id="element-3813" data-mp="37jv3dll68g9r">
              PKR {formatPKR(CUSTOMER.balance)}
            </span>
          </div>
        </div>

        {/* Beneficiary */}
        <div className={`rounded-lg p-5 mb-4 ${cardSurface}`} data-id="element-3814" data-mp="1w72d6f813zvt">
          <p className={`text-[11px] font-semibold uppercase tracking-wider mb-3 ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-3815" data-mp="e8e9260hopf8">
            Beneficiary
          </p>
          <div className="space-y-3" data-id="element-3816" data-mp="n92jkqqoc7nd">
            <div className="flex justify-between gap-3" data-id="element-3817" data-mp="n81ausx4rx8t">
              <span className={`text-[13px] ${textSec}`} data-id="element-3818" data-mp="j7hcjm0tmnl3">Organization</span>
              <span className={`text-[13px] font-semibold text-right ${textPri}`} data-id="element-3819" data-mp="j5cywcd4ea7m">
                {selectedOrg?.name}
              </span>
            </div>
            <div className="flex justify-between gap-3" data-id="element-3820" data-mp="3mpr1jz9g1ps9">
              <span className={`text-[13px] ${textSec}`} data-id="element-3821" data-mp="4aq7ox8dojo3">Account No.</span>
              <span className={`text-[13px] font-semibold text-right ${textPri}`} data-id="element-3822" data-mp="3vow3lfk7s196">
                {selectedOrg?.account}
              </span>
            </div>
            <div className="flex justify-between gap-3" data-id="element-3823" data-mp="37wxd3oc71zbg">
              <span className={`text-[13px] ${textSec}`} data-id="element-3824" data-mp="33wx0884qtw6i">Donation Type</span>
              <span className={`text-[13px] font-semibold text-right ${textPri}`} data-id="element-3825" data-mp="1xyr17w6y709d">
                {donationType}
              </span>
            </div>
          </div>
        </div>

        {/* Amount input */}
        <div className="mb-4" data-id="element-3826" data-mp="27qn0vboomyfp">
          <label className={`block text-[13px] font-semibold mb-2 ${textPri}`} data-id="element-3827" data-mp="22ylntnjox3om">
            Amount (PKR)
          </label>
          <input type="text" inputMode="numeric" value={amount} onChange={e => setAmount(e.target.value.replace(/[^\d]/g, ''))} placeholder="Enter amount" className={`w-full h-[54px] px-4 rounded-lg text-[18px] font-semibold outline-none border-2 transition-colors ${textPri} ${amountError ? 'border-[#EF4444]' : 'border-transparent focus:border-[#F37024]'} ${cardSurface}`} data-id="element-3828" data-mp="2hmqcr99c6bfu" />
          {amountError && <p className="text-[12px] text-[#EF4444] mt-1.5 ml-1" data-id="element-3829" data-mp="rblobrt6ae8m">
              {amountError}
            </p>}
        </div>

        {/* Transaction summary */}
        <div className={`rounded-lg p-5 mb-4 ${cardSurface}`} data-id="element-3830" data-mp="330ry5qyix86q">
          <p className={`text-[11px] font-semibold uppercase tracking-wider mb-3 ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-3831" data-mp="37x0j5kzejvlt">
            Transaction Summary
          </p>
          <div className="space-y-3" data-id="element-3832" data-mp="2y65wifi8utzu">
            <div className="flex justify-between" data-id="element-3833" data-mp="2k7xsxoqpcufp">
              <span className={`text-[13px] ${textSec}`} data-id="element-3834" data-mp="2k763kvnp7sxv">Donation Amount</span>
              <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-3835" data-mp="2p7am0r862xqq">
                PKR {amountNum > 0 ? formatPKR(amountNum) : '0'}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-3836" data-mp="2f8l3qxcczb9y">
              <span className={`text-[13px] ${textSec}`} data-id="element-3837" data-mp="2f9czc85yh908">Service Charges</span>
              <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-3838" data-mp="9gxybb5db56f">
                PKR {formatPKR(serviceCharge)}
              </span>
            </div>
            <div className="flex justify-between pt-3 border-t border-black/5" data-id="element-3839" data-mp="1meeyjwdvy0lm">
              <span className={`text-[14px] font-bold ${textPri}`} data-id="element-3840" data-mp="1mcbxmhb72n24">
                Total Amount
              </span>
              <span className={`text-[14px] font-bold ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-3841" data-mp="1ddqkx2sem299">
                PKR {amountNum > 0 ? formatPKR(totalAmount) : '0'}
              </span>
            </div>
          </div>
        </div>

        {/* Security indicator */}
        <div className={`flex items-center gap-2.5 px-4 py-3 rounded-lg ${isLight ? 'bg-[#10B981]/8' : 'bg-[#10B981]/12'}`} data-id="element-3842" data-mp="213kunbhffapa">
          <ShieldCheckIcon className="w-5 h-5 text-[#10B981] shrink-0" strokeWidth={2} data-id="element-3843" data-mp="1wbb5vys1sgol" />
          <span className={`text-[12px] font-medium ${textPri}`} data-id="element-3844" data-mp="1rbf508end5d9">
            Secure Banking Transaction
          </span>
          <LockIcon className="w-4 h-4 text-[#10B981] ml-auto shrink-0" strokeWidth={2} data-id="element-3845" data-mp="2k26wwtooj7us" />
        </div>
      </div>

      <div className="px-6 mt-auto pt-6 flex justify-center" data-id="element-3846" data-mp="2remv2tlfykjf">
        <button onClick={() => navigateTo('otp')} disabled={!amountValid} style={{
        ...ctaStyle,
        opacity: !amountValid ? 0.5 : 1,
        cursor: !amountValid ? 'not-allowed' : 'pointer'
      }} className="active:scale-[0.98] transition-transform shadow-md" data-id="element-3847" data-mp="2wbwiy4t6mo55">
          Pay Now
        </button>
      </div>
    </motion.div>;
  // ===== Screen 4: OTP =====
  const renderOtp = () => <motion.div key="otp" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${pageBg}`} style={{
    fontFamily: 'Poppins, system-ui, sans-serif'
  }} data-id="element-3848" data-mp="3f97g9jv977hy">
      <Header title="Verification" onBackClick={() => navigateTo('review', -1)} data-id="element-3849" data-mp="3t0udblx0sk1s" />
      <div className="px-4 sm:px-6 mt-4 flex-1 max-w-md mx-auto w-full" data-id="element-3850" data-mp="1u1hyyowrf56">
        <h2 className={`text-[20px] font-light mb-2 ${textPri}`} data-id="element-3851" data-mp="3kd8c7ux8f2oh">Enter OTP</h2>
        <p className={`text-[14px] mb-8 ${textSec}`} data-id="element-3852" data-mp="3gcodimx0fr08">
          A 6-digit OTP has been sent to your registered mobile number.
        </p>

        <div className="grid grid-cols-6 gap-2 sm:gap-3 max-w-[340px] mx-auto mb-8" data-id="element-3853" data-mp="3u6p60yxdchs5">
          {otp.map((digit, index) => <input key={index} id={`zakat-otp-${index}`} type="password" inputMode="numeric" autoComplete="one-time-code" maxLength={index === 0 ? 6 : 1} value={digit} onChange={e => handleOtpChange(index, e.target.value)} onKeyDown={e => handleOtpKeyDown(index, e)} className={`
                h-12 sm:h-14
                w-full
                rounded-xl
                text-center
                text-lg sm:text-xl
                font-bold
                outline-none
                border-2
                transition-colors
                ${digit ? 'border-[#F37024] bg-[#F37024]/10' : cardSurface}
                ${textPri}
              `} data-id="element-3854" data-mp="31fnkmg331q06" data-mpi={index} />)}
        </div>

        <div className="flex items-center justify-center gap-1.5" data-id="element-3855" data-mp="1unkde37dzgps">
          <span className={`text-[13px] ${textSec}`} data-id="element-3856" data-mp="1zkatgqj6ycb7">
            Didn't receive the OTP?
          </span>
          {resendTimer > 0 ? <span className="text-[13px] font-semibold" style={{
          color: maroon,
          fontVariantNumeric: 'tabular-nums'
        }} data-id="element-3857" data-mp="2nbgjtzmrcmzr">
              Resend in 00:{String(resendTimer).padStart(2, '0')}
            </span> : <button onClick={() => setResendTimer(15)} className="text-[13px] font-bold text-[#F37024] hover:underline" data-id="element-3858" data-mp="2ihu4u0t4g70s">
              Resend
            </button>}
        </div>
      </div>

      <div className="px-6 mt-auto pt-6 flex justify-center" data-id="element-3859" data-mp="3kwrkthgfutal">
        <button onClick={() => navigateTo('success')} disabled={!otpComplete} style={{
        ...ctaStyle,
        opacity: !otpComplete ? 0.5 : 1,
        cursor: !otpComplete ? 'not-allowed' : 'pointer'
      }} className="active:scale-[0.98] transition-transform shadow-md" data-id="element-3860" data-mp="3ku5axb62s1p7">
          Submit
        </button>
      </div>
    </motion.div>;
  const handleDownloadReceipt = () => {
    window.print();
  };
  const handleShareReceipt = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Donation Receipt',
          text: `Donation of PKR ${amountNum} successfully sent to ${selectedOrg?.name}`
        });
      }
    } catch (error) {
      console.log(error);
    }
  };
  // ===== Screen 5: Success (canonical receipt) =====
  const renderSuccess = () => <TransactionReceipt amount={amountNum > 0 ? amountNum : 5000} refNumber={refNumber} toName={selectedOrg?.name ?? ''} toAccount={selectedOrg?.account} fromName={CUSTOMER.name} fromAccount={CUSTOMER.account} additionalInfo={[{
    label: 'Donation Type',
    value: donationType
  }, {
    label: 'Transaction ID',
    value: txnId
  }, {
    label: 'Reference No.',
    value: refNumber
  }]} onNavigateHome={onNavigateHome} onShare={handleShareReceipt} onSaveImage={handleDownloadReceipt} data-id="element-3861" data-mp="2e8b4xfh1l3js" />;
  // ===== Scheduled Payments: List =====
  const renderScheduledList = () => {
    const filtered = scheduledPayments.filter(p => p.payTo.toLowerCase().includes(schedSearch.toLowerCase()) || p.type.toLowerCase().includes(schedSearch.toLowerCase()));
    const active = filtered.filter(p => p.status === 'Active');
    const past = filtered.filter(p => p.status !== 'Active');
    return <motion.div key="scheduled_list" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-24 flex flex-col ${pageBg}`} style={{
      fontFamily: 'Poppins, system-ui, sans-serif'
    }} data-id="element-3862" data-mp="2ltg9pjwyjwit">
        <Header title="My Scheduled Payments" onBackClick={() => navigateTo('services', -1)} data-id="element-3863" data-mp="2pw4kvy959fiy" />

        <div className="px-6 mt-2 flex-1 overflow-y-auto" data-id="element-3864" data-mp="2zs7nf57wrg6f">
          <div className={`flex items-center gap-3 px-4 rounded-2xl h-[52px] mb-6 ${cardSurface}`} data-id="element-3865" data-mp="2zrxsd380aitv">
            <SearchIcon className={`w-5 h-5 shrink-0 ${textSec}`} strokeWidth={2} data-id="element-3866" data-mp="3dlfaqkoyhpib" />
            <input value={schedSearch} onChange={e => setSchedSearch(e.target.value)} placeholder="Search scheduled payments" className={`w-full bg-transparent outline-none text-[14px] ${textPri} placeholder:opacity-50`} data-id="element-3867" data-mp="3nh8xewwe2zdt" />
          </div>

          {active.length > 0 && <div className="mb-6" data-id="element-3868" data-mp="axo4akzlyph1">
              <h3 className={`text-[13px] font-bold uppercase tracking-wider mb-3 ${textSec}`} data-id="element-3869" data-mp="3s6bei2a4v2vh">
                Active Schedules
              </h3>
              <div className="space-y-3" data-id="element-3870" data-mp="1236z05hiddm">
                {active.map(p => <div key={p.id} className={`p-4 rounded-2xl ${cardSurface}`} data-id="element-3871" data-mp="3jcevgwc9m6ka">
                    <div className="flex justify-between items-start mb-2" data-id="element-3872" data-mp="3o5rduoqcph4l">
                      <div data-id="element-3873" data-mp="39gbq7hm49rt4">
                        <p className={`text-[15px] font-bold ${textPri}`} data-id="element-3874" data-mp="39h4odu70f8zn">
                          {p.payTo}
                        </p>
                        <p className={`text-[12px] ${textSec}`} data-id="element-3875" data-mp="3ecjzwfhjossw">
                          {p.type} • {p.frequency}
                        </p>
                      </div>
                      <p className={`text-[15px] font-bold ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-3876" data-mp="34g76zjttqiz7">
                        PKR {formatPKR(p.amount)}
                      </p>
                    </div>
                    <div className="flex justify-between items-center mt-4 pt-3 border-t border-black/5 dark:border-white/5" data-id="element-3877" data-mp="21cc35z1ebwos">
                      <p className={`text-[11px] ${textSec}`} data-id="element-3878" data-mp="26cq30tsug0bq">
                        Next: {p.startDate} at {p.time}
                      </p>
                      <button onClick={() => {
                  setSelectedSched(p);
                  navigateTo('scheduled_detail');
                }} className={`text-[12px] font-bold px-4 py-1.5 rounded-full border ${isLight ? 'border-[#5A1408] text-[#5A1408]' : 'border-[#F37024] text-[#F37024]'}`} data-id="element-3879" data-mp="2fcmz3ghkc1gt">
                        Review
                      </button>
                    </div>
                  </div>)}
              </div>
            </div>}

          {past.length > 0 && <div data-id="element-3880" data-mp="iefldsvv23le">
              <h3 className={`text-[13px] font-bold uppercase tracking-wider mb-3 ${textSec}`} data-id="element-3881" data-mp="idxr31srdzg4">
                Past / Failed
              </h3>
              <div className="space-y-3" data-id="element-3882" data-mp="8g9k9dhvp1hl">
                {past.map(p => <div key={p.id} className={`p-4 rounded-2xl opacity-75 ${cardSurface}`} data-id="element-3883" data-mp="8ick0p8u28zi">
                    <div className="flex justify-between items-start mb-2" data-id="element-3884" data-mp="115q8s02dny8j">
                      <div data-id="element-3885" data-mp="wdf98ybjuegi">
                        <p className={`text-[15px] font-bold ${textPri}`} data-id="element-3886" data-mp="wcnpqlv8876l">
                          {p.payTo}
                        </p>
                        <p className={`text-[12px] ${textSec}`} data-id="element-3887" data-mp="rd1bqa7kqmwq">{p.type}</p>
                      </div>
                      <div className="text-right" data-id="element-3888" data-mp="1ow2s0732phef">
                        <p className={`text-[15px] font-bold ${textPri}`} data-id="element-3889" data-mp="1k2hxwq4lp7ki">
                          PKR {formatPKR(p.amount)}
                        </p>
                        <p className={`text-[10px] font-bold mt-0.5 ${p.status === 'Failed' ? 'text-[#EF4444]' : 'text-[#22C55E]'}`} data-id="element-3890" data-mp="3icyy0712p8mp">
                          {p.status}
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-between items-center mt-4 pt-3 border-t border-black/5 dark:border-white/5" data-id="element-3891" data-mp="2une44t3dsqyn">
                      <p className={`text-[11px] ${textSec}`} data-id="element-3892" data-mp="2zmqzy3c8d8dd">
                        Date: {p.startDate}
                      </p>
                      <button onClick={() => {
                  setSelectedSched(p);
                  navigateTo('scheduled_detail');
                }} className={`text-[12px] font-bold px-4 py-1.5 rounded-full border ${isLight ? 'border-[#5A1408] text-[#5A1408]' : 'border-[#F37024] text-[#F37024]'}`} data-id="element-3893" data-mp="2loamltjv4jm9">
                        Review
                      </button>
                    </div>
                  </div>)}
              </div>
            </div>}

          {filtered.length === 0 && <p className={`text-center text-[13px] mt-8 ${textSec}`} data-id="element-3894" data-mp="17ssr2ymwrcfb">
              No scheduled payments found.
            </p>}
        </div>

        <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] p-6 bg-gradient-to-t from-black/5 to-transparent flex justify-center" data-id="element-3895" data-mp="1hnt0rs4sh0k8">
          <button onClick={() => {
          setSchedTab('once');
          setSchedDate('');
          setSchedEndDate('');
          setSchedTime('12:00 AM');
          setSchedTerms(false);
          setSchedAmount('');
          setSchedPayTo('');
          setSchedType('');
          setSchedFreq('Monthly');
          setSchedCustomDays('');
          setSchedComments('');
          setIsEditingSched(false);
          navigateTo('scheduled_new');
        }} style={{
          ...ctaStyle,
          gap: '8px'
        }} className="shadow-lg active:scale-[0.98] transition-transform" data-id="element-3896" data-mp="1qpk5ouri93d1">
            <PlusIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-3897" data-mp="7qw03zym6gtx" />
            Schedule a New Payment
          </button>
        </div>
      </motion.div>;
  };
  // ===== Scheduled Payments: New/Edit =====
  const renderScheduledNew = () => {
    const isRecurring = schedTab === 'recurring';
    // Validation
    const amountNum = Number(schedAmount);
    const isValidAmount = schedAmount !== '' && amountNum > 0 && amountNum <= CUSTOMER.balance;
    const baseValid = isValidAmount && schedDate !== '' && schedTime !== '' && schedPayTo !== '' && schedType !== '' && schedTerms;
    const isValid = isRecurring ? baseValid && schedEndDate !== '' : baseValid;
    // Total Amount calculation for recurring
    let totalAmount = amountNum;
    let durationMsg = '';
    if (isRecurring && isValidAmount && schedDate && schedEndDate) {
      // Mock calculation for UI
      const occurrences = schedFreq === 'Weekly' ? 12 : schedFreq === 'Monthly' ? 3 : schedFreq === 'Quarterly' ? 1 : 5;
      totalAmount = amountNum * occurrences;
      durationMsg = `Payment scheduled for ${occurrences} occurrences`;
    }
    return <motion.div key="scheduled_new" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-24 flex flex-col ${pageBg}`} style={{
      fontFamily: 'Poppins, system-ui, sans-serif'
    }} data-id="element-3898" data-mp="1x7q41pmyvcme">
        <Header title={isEditingSched ? 'Edit Schedule' : 'Schedule Payment'} onBackClick={() => navigateTo(isEditingSched ? 'scheduled_detail' : 'scheduled_list', -1)} data-id="element-3899" data-mp="1j7fbmi60dpz4" />

        {!isEditingSched && <div className="px-6 mt-2" data-id="element-3900" data-mp="18og4lgvjyej1">
            <div className={`relative flex rounded-2xl p-1 ${isLight ? 'bg-[#F5EBE6]' : 'bg-[#2D1810]'}`} data-id="element-3901" data-mp="unlsoo9apvfm">
              <motion.div layout className="absolute top-1 bottom-1 rounded-xl bg-[#F37024] shadow-md" initial={false} animate={{
            left: schedTab === 'once' ? 4 : '50%',
            right: schedTab === 'once' ? '50%' : 4
          }} transition={{
            type: 'spring',
            damping: 28,
            stiffness: 280
          }} data-id="element-3902" data-mp="zn854vhav7nd" />
              <button onClick={() => setSchedTab('once')} className={`relative z-10 flex-1 py-2.5 text-[13px] font-semibold rounded-xl transition-colors ${schedTab === 'once' ? 'text-white' : textSec}`} data-id="element-3903" data-mp="269u6q1zxk3j2">
                Set for Once
              </button>
              <button onClick={() => setSchedTab('recurring')} className={`relative z-10 flex-1 py-2.5 text-[13px] font-semibold rounded-xl transition-colors ${schedTab === 'recurring' ? 'text-white' : textSec}`} data-id="element-3904" data-mp="3qruvzvx9f29s">
                Recurring
              </button>
            </div>
          </div>}

        <div className="px-6 mt-6 flex-1 overflow-y-auto space-y-5" data-id="element-3905" data-mp="37u1vt1e0t6g3">
          {isRecurring && <div data-id="element-3906" data-mp="3lttg1jv4rj2j">
              <label className={`block text-[13px] font-semibold mb-2 ${textPri}`} data-id="element-3907" data-mp="3luco88uzat3d">
                Set Frequency
              </label>
              <div className="flex flex-wrap gap-2" data-id="element-3908" data-mp="3gx31x22vyxjr">
                {['Weekly', 'Monthly', 'Quarterly', 'Custom'].map(f => <button key={f} onClick={() => setSchedFreq(f)} className={`px-4 py-2 rounded-xl text-[12px] font-bold transition-colors ${schedFreq === f ? 'bg-[#F37024] text-white' : `${cardSurface} ${textSec}`}`} data-id="element-3909" data-mp="w50vrlajbp1c">
                    {f}
                  </button>)}
              </div>
              {schedFreq === 'Custom' && <div className="mt-3" data-id="element-3910" data-mp="1tytsm0pl2ii0">
                  <input type="text" value={schedCustomDays} onChange={e => setSchedCustomDays(e.target.value.replace(/[^0-9]/g, ''))} placeholder="Enter No. of Days" className={`w-full h-[48px] px-4 rounded-xl text-[14px] outline-none border transition-colors ${textPri} focus:border-[#F37024] ${cardSurface}`} data-id="element-3911" data-mp="1tzv7yhdmtr5j" />
                  <div className="flex flex-wrap gap-2 mt-2" data-id="element-3912" data-mp="ej33kz2omo1e">
                    {['Daily', '15 Days', '60 Days', '180 Days', '365 Days'].map(d => <button key={d} onClick={() => setSchedCustomDays(d.split(' ')[0])} className={`px-3 py-1.5 rounded-lg text-[11px] font-medium ${cardSurface} ${textSec}`} data-id="element-3913" data-mp="672t2wo06ki6">
                        {d}
                      </button>)}
                  </div>
                </div>}
            </div>}

          <div className="grid grid-cols-2 gap-3" data-id="element-3914" data-mp="kxicq70rtke8">
            <div data-id="element-3915" data-mp="kw7eemq3h4ui">
              <label className={`block text-[13px] font-semibold mb-2 ${textPri}`} data-id="element-3916" data-mp="kvp970lgfgag">
                {isRecurring ? 'Start Date' : 'Set Date'}
              </label>
              <input type="date" value={schedDate} onChange={e => setSchedDate(e.target.value)} className={`w-full h-[48px] px-3 rounded-xl text-[14px] outline-none border transition-colors ${textPri} focus:border-[#F37024] ${cardSurface}`} data-id="element-3917" data-mp="35p2ge9fv9iga" />
            </div>
            {isRecurring && <div data-id="element-3918" data-mp="2k3flqxb5neh6">
                <label className={`block text-[13px] font-semibold mb-2 ${textPri}`} data-id="element-3919" data-mp="2k2xhbdec9ty6">
                  End Date
                </label>
                <input type="date" value={schedEndDate} onChange={e => setSchedEndDate(e.target.value)} className={`w-full h-[48px] px-3 rounded-xl text-[14px] outline-none border transition-colors ${textPri} focus:border-[#F37024] ${cardSurface}`} data-id="element-3920" data-mp="2a7cifo4kh9sn" />
              </div>}
            {!isRecurring && <div data-id="element-3921" data-mp="1rervsofgo0xa">
                <label className={`block text-[13px] font-semibold mb-2 ${textPri}`} data-id="element-3922" data-mp="1rf1l09som41x">
                  Set Time
                </label>
                <input type="time" value={schedTime} onChange={e => setSchedTime(e.target.value)} className={`w-full h-[48px] px-3 rounded-xl text-[14px] outline-none border transition-colors ${textPri} focus:border-[#F37024] ${cardSurface}`} data-id="element-3923" data-mp="1cbrsfq7qozz0" />
              </div>}
          </div>

          {isRecurring && <div data-id="element-3924" data-mp="tnmvp2pi2f9p">
              <label className={`block text-[13px] font-semibold mb-2 ${textPri}`} data-id="element-3925" data-mp="to4uo73jr7lb">
                Set Time
              </label>
              <input type="time" value={schedTime} onChange={e => setSchedTime(e.target.value)} className={`w-full h-[48px] px-3 rounded-xl text-[14px] outline-none border transition-colors ${textPri} focus:border-[#F37024] ${cardSurface}`} data-id="element-3926" data-mp="13jgeh3ttb4zx" />
              {durationMsg && <p className={`text-[12px] font-bold mt-2 ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-3927" data-mp="fskhlsai1jlt">
                  {durationMsg}
                </p>}
            </div>}

          <div data-id="element-3928" data-mp="hiz9oaoh1yh2">
            <label className={`block text-[13px] font-semibold mb-2 ${textPri}`} data-id="element-3929" data-mp="hipm0tqwg3ej">
              Amount
            </label>
            <input type="text" inputMode="numeric" value={schedAmount} onChange={e => setSchedAmount(e.target.value.replace(/[^\d]/g, ''))} placeholder="Enter Amount" className={`w-full h-[54px] px-4 rounded-xl text-[18px] font-bold outline-none border-2 transition-colors ${textPri} ${schedAmount !== '' && amountNum > CUSTOMER.balance ? 'border-[#EF4444]' : 'border-transparent focus:border-[#F37024]'} ${cardSurface}`} data-id="element-3930" data-mp="3l0hh4h0c13gy" />
            {schedAmount !== '' && amountNum > CUSTOMER.balance && <div className="flex items-center gap-1.5 mt-1.5 text-[#EF4444]" data-id="element-3931" data-mp="1csuqbsz5rdf2">
                <AlertTriangleIcon className="w-4 h-4" data-id="element-3932" data-mp="1hrxp4vojonel" />
                <p className="text-[11px] font-medium" data-id="element-3933" data-mp="1htijak6y64bx">
                  Amount exceeds transaction limit
                </p>
              </div>}
            <div className="flex gap-2 mt-3" data-id="element-3934" data-mp="17t9iet20fuq7">
              {['200', '500', '1000', '5000'].map(amt => <button key={amt} onClick={() => setSchedAmount(amt)} className={`flex-1 py-2 rounded-lg text-[12px] font-bold transition-colors ${schedAmount === amt ? 'bg-[#F37024] text-white' : `${cardSurface} ${textSec}`}`} data-id="element-3935" data-mp="1vkn9ywsc7ma4">
                  {amt}
                </button>)}
            </div>
          </div>

          {isRecurring && schedAmount !== '' && <div className={`p-4 rounded-xl ${cardSurface}`} data-id="element-3936" data-mp="2egn34ax1wvva">
              <p className={`text-[11px] uppercase tracking-wider ${textSec}`} data-id="element-3937" data-mp="2jarc0zpvndz0">
                Total Scheduled Amount
              </p>
              <p className={`text-[18px] font-bold mt-1 ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-3938" data-mp="viiiidltmu0r">
                PKR {formatPKR(totalAmount)}
              </p>
            </div>}

          <div data-id="element-3939" data-mp="hlvhnzqqzjpk">
            <label className={`block text-[13px] font-semibold mb-2 ${textPri}`} data-id="element-3940" data-mp="hm56vl3yxmu7">
              Pay To
            </label>
            <select value={schedPayTo} onChange={e => setSchedPayTo(e.target.value)} className={`w-full h-[48px] px-3 rounded-xl text-[14px] outline-none border transition-colors ${textPri} focus:border-[#F37024] ${cardSurface} appearance-none`} data-id="element-3941" data-mp="19c9q3gfzs3tu">
              <option value="" disabled data-id="element-3942" data-mp="1x1ub04vphb5r">
                Select Organization
              </option>
              <option value="Al-khidmat Foundation" data-id="element-3943" data-mp="1sb5kosvrik87">
                Al-khidmat Foundation
              </option>
              <option value="Edhi Foundation" data-id="element-3944" data-mp="3ayf4froehl24">Edhi Foundation</option>
              <option value="Storm Fiber" data-id="element-3945" data-mp="3b08cnbenwdc8">Storm Fiber</option>
              <option value="LESCO" data-id="element-3946" data-mp="3fxrrodj6ou58">LESCO</option>
            </select>
          </div>

          <div data-id="element-3947" data-mp="3jxiooyzb06i7">
            <label className={`block text-[13px] font-semibold mb-2 ${textPri}`} data-id="element-3948" data-mp="3jx0k9f2hmlz7">
              Pay From
            </label>
            <select value={schedPayFrom} onChange={e => setSchedPayFrom(e.target.value)} className={`w-full h-[48px] px-3 rounded-xl text-[14px] outline-none border transition-colors ${textPri} focus:border-[#F37024] ${cardSurface} appearance-none`} data-id="element-3949" data-mp="3twzrn8n686ah">
              <option value="Kashish Shewani / 1234 **** **** 6544" data-id="element-3950" data-mp="bja1k6fjw7xf">
                Kashish Shewani / 1234 **** **** 6544
              </option>
              <option value="Platinum Credit Card" data-id="element-3951" data-mp="gjocpxcg7xzo">Platinum Credit Card</option>
            </select>
            <p className={`text-[11px] mt-1.5 ${textSec}`} data-id="element-3952" data-mp="lh7qyx95carm">
              Available Balance: PKR {formatPKR(CUSTOMER.balance)}
            </p>
          </div>

          <div data-id="element-3953" data-mp="2clrfu6tj7va1">
            <label className={`block text-[13px] font-semibold mb-2 ${textPri}`} data-id="element-3954" data-mp="2cmay5on3glol">
              Payment Type
            </label>
            <select value={schedType} onChange={e => setSchedType(e.target.value)} className={`w-full h-[48px] px-3 rounded-xl text-[14px] outline-none border transition-colors ${textPri} focus:border-[#F37024] ${cardSurface} appearance-none`} data-id="element-3955" data-mp="2mj5wo7jdam2v">
              <option value="" disabled data-id="element-3956" data-mp="3a8i2cxfhkc0f">
                Select Reason
              </option>
              <option value="Zakat" data-id="element-3957" data-mp="359nj5udlwlfs">Zakat</option>
              <option value="Donation" data-id="element-3958" data-mp="35aynpwevlx7y">Donation</option>
              <option value="Internet Bill" data-id="element-3959" data-mp="1fuh2qac43kza">Internet Bill</option>
              <option value="Utility Bill" data-id="element-3960" data-mp="1fwtj4j281na8">Utility Bill</option>
            </select>
          </div>

          <label className="flex items-start gap-3 cursor-pointer mt-2" data-id="element-3961" data-mp="1axqq5wzin7im">
            <div className="mt-0.5" data-id="element-3962" data-mp="3mkanrj716xs6">
              <div onClick={() => setSchedTerms(!schedTerms)} className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${schedTerms ? 'bg-[#F37024] border-[#F37024]' : isDark ? 'border-[#3D2518]' : 'border-[#E5D5C5]'}`} data-id="element-3963" data-mp="3mlc33zv2y6fp">
                {schedTerms && <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} data-id="element-3964" data-mp="33s8fow8ggptj" />}
              </div>
            </div>
            <p className={`text-[12px] leading-snug ${textSec}`} data-id="element-3965" data-mp="38t5vj25zt7rv">
              I have read and agreed to the{' '}
              <span className="text-[#F37024] underline" data-id="element-3966" data-mp="2twevcqohvs17">
                Terms and Conditions
              </span>
              .
            </p>
          </label>
        </div>

        <div className="px-6 mt-auto pt-6 flex justify-center" data-id="element-3967" data-mp="s3gwoe81e58c">
          <button onClick={() => navigateTo('scheduled_review')} disabled={!isValid} style={{
          ...ctaStyle,
          opacity: !isValid ? 0.5 : 1,
          cursor: !isValid ? 'not-allowed' : 'pointer'
        }} className="active:scale-[0.98] transition-transform shadow-md" data-id="element-3968" data-mp="x2k2i0vucfhd">
            {isEditingSched ? 'Update Schedule' : 'NEXT'}
          </button>
        </div>
      </motion.div>;
  };
  // ===== Scheduled Payments: Review =====
  const renderScheduledReview = () => {
    const isRecurring = schedTab === 'recurring';
    const amountNum = Number(schedAmount);
    const occurrences = schedFreq === 'Weekly' ? 12 : schedFreq === 'Monthly' ? 3 : schedFreq === 'Quarterly' ? 1 : 5;
    const totalAmount = isRecurring ? amountNum * occurrences : amountNum;
    return <motion.div key="scheduled_review" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-24 flex flex-col ${pageBg}`} style={{
      fontFamily: 'Poppins, system-ui, sans-serif'
    }} data-id="element-3969" data-mp="mgpqw7qydenl">
        <Header title="Review Schedule" onBackClick={() => navigateTo('scheduled_new', -1)} data-id="element-3970" data-mp="tz73wny1fblb" />

        <div className="px-6 mt-2 flex-1 overflow-y-auto space-y-4" data-id="element-3971" data-mp="13rwbv8udq7bw">
          <div className={`rounded-lg p-5 ${cardSurface}`} data-id="element-3972" data-mp="13t7gfavnfj42">
            <p className={`text-[11px] font-semibold uppercase tracking-wider mb-4 ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-3973" data-mp="64w8pl99svsh">
              Payment Details
            </p>
            <div className="space-y-3" data-id="element-3974" data-mp="g07f9gaiywp5">
              <div className="flex justify-between" data-id="element-3975" data-mp="l0vgsvwdqvxm">
                <span className={`text-[13px] ${textSec}`} data-id="element-3976" data-mp="l2gakjaveauf">
                  Scheduled Amount
                </span>
                <span className={`text-[14px] font-bold ${textPri}`} data-id="element-3977" data-mp="1rgnup9m2xdc0">
                  PKR {formatPKR(amountNum)}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3978" data-mp="2l3k6hzx46958">
                <span className={`text-[13px] ${textSec}`} data-id="element-3979" data-mp="2l66dnyg3emmx">Pay From</span>
                <span className={`text-[13px] font-semibold text-right max-w-[180px] ${textPri}`} data-id="element-3980" data-mp="2q2f0zz4g1m56">
                  {schedPayFrom}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3981" data-mp="2gban72qi87bx">
                <span className={`text-[13px] ${textSec}`} data-id="element-3982" data-mp="22d2rsz949e2v">Pay To</span>
                <span className={`text-[13px] font-semibold text-right max-w-[180px] ${textPri}`} data-id="element-3983" data-mp="27cfffm7tap6i">
                  {schedPayTo} / 1234 **** **** 6544
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3984" data-mp="1xdhipw08fpcf">
                <span className={`text-[13px] ${textSec}`} data-id="element-3985" data-mp="1xg2m4pg6idca">Payment Type</span>
                <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-3986" data-mp="3nr6z9aqw6o47">
                  {schedType}
                </span>
              </div>
            </div>
          </div>

          <div className={`rounded-lg p-5 ${cardSurface}`} data-id="element-3987" data-mp="1g5yltejheqvg">
            <p className={`text-[11px] font-semibold uppercase tracking-wider mb-4 ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-3988" data-mp="1c3ljz17uvd3y">
              Schedule Details
            </p>
            <div className="space-y-3" data-id="element-3989" data-mp="1l3i2sm7kdhr3">
              <div className="flex justify-between" data-id="element-3990" data-mp="1l2grqhqjsv9d">
                <span className={`text-[13px] ${textSec}`} data-id="element-3991" data-mp="1zso2ebabd9zi">Frequency</span>
                <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-3992" data-mp="1zur0zjpj8bfu">
                  {isRecurring ? schedFreq === 'Custom' ? `${schedCustomDays} Days` : schedFreq : 'Once'}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3993" data-mp="efl7k8p7cui0">
                <span className={`text-[13px] ${textSec}`} data-id="element-3994" data-mp="egct0qp35buk">
                  {isRecurring ? 'Start Date' : 'Scheduled Date'}
                </span>
                <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-3995" data-mp="29iq0kjiz1v02">
                  {schedDate}
                </span>
              </div>
              {isRecurring && <div className="flex justify-between" data-id="element-3996" data-mp="2ejx8cil46u6q">
                  <span className={`text-[13px] ${textSec}`} data-id="element-3997" data-mp="2jfulkg12zkfa">End Date</span>
                  <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-3998" data-mp="2nj0pvi4mv1j4">
                    {schedEndDate}
                  </span>
                </div>}
              <div className="flex justify-between" data-id="element-3999" data-mp="2sfrg2ya5jdi9">
                <span className={`text-[13px] ${textSec}`} data-id="element-4000" data-mp="2xfncocgis2ns">Scheduled Time</span>
                <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-4001" data-mp="3275batv2lr5l">
                  {schedTime}
                </span>
              </div>
              {isRecurring && <>
                  <div className="flex justify-between pt-3 border-t border-black/5 dark:border-white/5" data-id="element-4002" data-mp="3b8v2pzoycq3d">
                    <span className={`text-[13px] font-bold ${textPri}`} data-id="element-4003" data-mp="3g8803edg9fk7">
                      Estimated Total
                    </span>
                    <span className={`text-[14px] font-bold ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-4004" data-mp="14hqdwr0l85jd">
                      PKR {formatPKR(totalAmount)}
                    </span>
                  </div>
                  <p className={`text-[11px] text-right mt-1 ${textSec}`} data-id="element-4005" data-mp="qktrd1ri5a8a">
                    Payment scheduled for {occurrences} occurrences
                  </p>
                </>}
            </div>
          </div>

          <div data-id="element-4006" data-mp="1j98zr5g4ouan">
            <label className={`block text-[13px] font-semibold mb-2 ${textPri}`} data-id="element-4007" data-mp="1jatvgye6ija3">
              Comments (Optional)
            </label>
            <textarea value={schedComments} onChange={e => setSchedComments(e.target.value)} placeholder="Add a note..." rows={2} className={`w-full p-3 rounded-xl text-[13px] outline-none border transition-colors resize-none ${textPri} focus:border-[#F37024] ${cardSurface}`} data-id="element-4008" data-mp="19i4nidhu7nji" />
          </div>
        </div>

        <div className="px-6 mt-auto pt-6 flex justify-center" data-id="element-4009" data-mp="1qs89hbh2prah">
          <button onClick={() => navigateTo('scheduled_mpin')} style={ctaStyle} className="active:scale-[0.98] transition-transform shadow-md" data-id="element-4010" data-mp="1crnrghizfzi1">
            CONFIRM
          </button>
        </div>
      </motion.div>;
  };
  // ===== Scheduled Payments: MPIN =====
  const renderScheduledMpin = () => {
    const isComplete = schedMpin.every(d => d !== '');
    return <motion.div key="scheduled_mpin" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${pageBg}`} style={{
      fontFamily: 'Poppins, system-ui, sans-serif'
    }} data-id="element-4011" data-mp="1vr1ppbnqvwec">
        <Header title="Authentication" onBackClick={() => navigateTo('scheduled_review', -1)} data-id="element-4012" data-mp="2t6yr9h85kcr4" />
        <div className="px-6 mt-4 flex-1 flex flex-col items-center justify-center" data-id="element-4013" data-mp="32i39slp5hsqn">
          <div className="w-16 h-16 rounded-full bg-[#F37024]/10 flex items-center justify-center mb-6" data-id="element-4014" data-mp="2ygrqexwve3qw">
            <LockIcon className="w-8 h-8 text-[#F37024]" strokeWidth={2} data-id="element-4015" data-mp="3cc3lx7iuny8v" />
          </div>
          <h2 className={`text-[20px] font-bold mb-2 ${textPri}`} data-id="element-4016" data-mp="37d94a8wmcfqc">
            Enter MPIN
          </h2>
          <p className={`text-[14px] mb-8 text-center ${textSec}`} data-id="element-4017" data-mp="2jnobkyany43v">
            Enter your 4-digit MPIN to confirm this scheduled payment.
          </p>
          <div className="flex gap-3 mb-12" data-id="element-4018" data-mp="2tkswkusrvtj3">
            {[0, 1, 2, 3].map(index => <input key={index} id={`sched-mpin-${index}`} type="password" inputMode="numeric" maxLength={1} value={schedMpin[index]} onChange={e => {
            const val = e.target.value.replace(/[^\d]/g, '');
            const next = [...schedMpin];
            next[index] = val;
            setSchedMpin(next);
            if (val && index < 3) document.getElementById(`sched-mpin-${index + 1}`)?.focus();
            if (next.every(d => d !== '')) {
              setTimeout(() => navigateTo('scheduled_success'), 400);
            }
          }} onKeyDown={e => {
            if (e.key === 'Backspace' && !schedMpin[index] && index > 0) {
              document.getElementById(`sched-mpin-${index - 1}`)?.focus();
            }
          }} className={`w-14 h-14 rounded-xl text-center text-xl font-bold outline-none border-2 transition-colors ${schedMpin[index] ? 'border-[#F37024] bg-[#F37024]/10' : cardSurface} ${textPri}`} data-id="element-4019" data-mp="2okeya4gz3xy9" />)}
          </div>
        </div>
      </motion.div>;
  };
  // ===== Scheduled Payments: Success (canonical receipt) =====
  const renderScheduledSuccess = () => {
    const isRecurring = schedTab === 'recurring';
    const amountNum = Number(schedAmount);
    const refNumber = `SCH${Date.now().toString().slice(-9)}`;
    return <TransactionReceipt amount={amountNum} refNumber={refNumber} toName={schedPayTo} toAccount="1234 **** **** 6544" fromName={CUSTOMER.name} fromAccount={CUSTOMER.account} additionalInfo={[{
      label: 'Payment Type',
      value: schedType
    }, {
      label: 'Frequency',
      value: isRecurring ? schedFreq === 'Custom' ? `${schedCustomDays} Days` : schedFreq : 'Once'
    }, {
      label: 'Date & Time',
      value: `${schedDate} ${schedTime}`
    }, {
      label: 'Reference No.',
      value: refNumber
    }]} onNavigateHome={() => navigateTo('scheduled_list')} onShare={handleShareReceipt} onSaveImage={handleDownloadReceipt} data-id="element-4020" data-mp="2t1lusa3ggh2r" />;
  };
  // ===== Scheduled Payments: Detail View =====
  const renderScheduledDetail = () => {
    if (!selectedSched) return null;
    const isRecurring = selectedSched.frequency !== 'Once';
    const occurrences = selectedSched.frequency === 'Weekly' ? 12 : selectedSched.frequency === 'Monthly' ? 3 : selectedSched.frequency === 'Quarterly' ? 1 : 5;
    const totalAmount = isRecurring ? selectedSched.amount * occurrences : selectedSched.amount;
    return <motion.div key="scheduled_detail" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-24 flex flex-col ${pageBg}`} style={{
      fontFamily: 'Poppins, system-ui, sans-serif'
    }} data-id="element-4021" data-mp="2wji14t3jhai9">
        <Header title="Schedule Details" onBackClick={() => navigateTo('scheduled_list', -1)} data-id="element-4022" data-mp="23y34xcqdqzo1" />

        <div className="px-6 mt-2 flex-1 overflow-y-auto space-y-4" data-id="element-4023" data-mp="2cyhreff2ykt4">
          <div className="text-center mb-6" data-id="element-4024" data-mp="2czsxilw004ne">
            <p className={`text-[13px] ${textSec}`} data-id="element-4025" data-mp="27ywtm8bncihz">Scheduled Amount</p>
            <p className={`text-[32px] font-bold mt-1 ${isLight ? 'text-[#5A1408]' : 'text-[#F37024]'}`} data-id="element-4026" data-mp="281rc3ub3nt8r">
              PKR {formatPKR(selectedSched.amount)}
            </p>
            <span className={`inline-block mt-2 px-3 py-1 rounded-full text-[11px] font-bold ${selectedSched.status === 'Active' ? 'bg-[#22C55E]/10 text-[#22C55E]' : 'bg-[#EF4444]/10 text-[#EF4444]'}`} data-id="element-4027" data-mp="2hvro8g4styqz">
              {selectedSched.status}
            </span>
          </div>

          <div className={`rounded-2xl p-5 ${cardSurface}`} data-id="element-4028" data-mp="35ku8zwuqc7pp">
            <div className="space-y-4" data-id="element-4029" data-mp="30pn6uth3zb2w">
              <div className="flex items-start gap-3" data-id="element-4030" data-mp="3d6tqr7w7o6j7">
                <div className="w-2 h-2 rounded-full bg-[#F37024] mt-1.5 shrink-0" data-id="element-4031" data-mp="3d58wzkhq0rme" />
                <div className="flex-1" data-id="element-4032" data-mp="386pcloirznw5">
                  <p className={`text-[11px] uppercase tracking-wider ${textSec}`} data-id="element-4033" data-mp="38a3desbsjp1h">
                    Pay From
                  </p>
                  <p className={`text-[14px] font-bold ${textPri}`} data-id="element-4034" data-mp="3i6g4difybosj">
                    {selectedSched.payFrom}
                  </p>
                  <p className={`text-[12px] ${textSec}`} data-id="element-4035" data-mp="2ph98kr7vtdaz">
                    {selectedSched.payFromAccount}
                  </p>
                </div>
              </div>
              <div className="w-px h-6 bg-gray-300 ml-1 -my-2" data-id="element-4036" data-mp="347gigijtpo02" />
              <div className="flex items-start gap-3" data-id="element-4037" data-mp="2zedx2xp79kuy">
                <div className="w-2 h-2 rounded-full bg-gray-400 mt-1.5 shrink-0" data-id="element-4038" data-mp="2zdkl9ibdw55y" />
                <div className="flex-1" data-id="element-4039" data-mp="2bnqbj9md2wpx">
                  <p className={`text-[11px] uppercase tracking-wider ${textSec}`} data-id="element-4040" data-mp="26r9pckyq2sfh">
                    Pay To
                  </p>
                  <p className={`text-[14px] font-bold ${textPri}`} data-id="element-4041" data-mp="m6w7zl1x82xz">
                    {selectedSched.payTo}
                  </p>
                  <p className={`text-[12px] ${textSec}`} data-id="element-4042" data-mp="cbaxszmp7wd5">
                    {selectedSched.payToAccount}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className={`rounded-2xl p-5 ${cardSurface}`} data-id="element-4043" data-mp="14yxcp41dmnfb">
            <div className="space-y-3" data-id="element-4044" data-mp="14xdrcz0v5m6o">
              <div className="flex justify-between" data-id="element-4045" data-mp="19x8ldbfxqrvy">
                <span className={`text-[13px] ${textSec}`} data-id="element-4046" data-mp="19vxhlbmhpk4u">Payment Type</span>
                <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-4047" data-mp="vy8ya882ml1w">
                  {selectedSched.type}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-4048" data-mp="3gohthxis0n6i">
                <span className={`text-[13px] ${textSec}`} data-id="element-4049" data-mp="3gqu9w68vyphg">Frequency</span>
                <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-4050" data-mp="3krwbgtq9zdml">
                  {selectedSched.frequency}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-4051" data-mp="1m7zlkzwr3as9">
                <span className={`text-[13px] ${textSec}`} data-id="element-4052" data-mp="1m9ax5lwftmtx">Start Date</span>
                <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-4053" data-mp="1hghud3kj4okh">
                  {selectedSched.startDate}
                </span>
              </div>
              {isRecurring && selectedSched.endDate && <div className="flex justify-between" data-id="element-4054" data-mp="18ifpgcxodbrx">
                  <span className={`text-[13px] ${textSec}`} data-id="element-4055" data-mp="13hrxnp1mgqwn">End Date</span>
                  <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-4056" data-mp="yiyfvkfaydqo">
                    {selectedSched.endDate}
                  </span>
                </div>}
              <div className="flex justify-between" data-id="element-4057" data-mp="tm8439aj64b2">
                <span className={`text-[13px] ${textSec}`} data-id="element-4058" data-mp="2svws49kkbkgn">Scheduled Time</span>
                <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-4059" data-mp="2nxd6ka9vsaot">
                  {selectedSched.time}
                </span>
              </div>
              {isRecurring && <div className="flex justify-between" data-id="element-4060" data-mp="30g8s09mhbjl3">
                  <span className={`text-[13px] ${textSec}`} data-id="element-4061" data-mp="30enxgk0600n8">
                    Estimated Total
                  </span>
                  <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-4062" data-mp="359jrfpoyoy2z">
                    PKR {formatPKR(totalAmount)}
                  </span>
                </div>}
              <div className="flex justify-between" data-id="element-4063" data-mp="2di46e031zjev">
                <span className={`text-[13px] ${textSec}`} data-id="element-4064" data-mp="2hio0svw8s8xb">Created On</span>
                <span className={`text-[13px] font-semibold ${textPri}`} data-id="element-4065" data-mp="2mgz9h3aswddy">
                  {selectedSched.createdOn}
                </span>
              </div>
            </div>
          </div>

          {/* Collapsible sections mock */}
          <details className={`rounded-2xl ${cardSurface} group`} data-id="element-4066" data-mp="1pp379j2xorm">
            <summary className="p-4 flex justify-between items-center cursor-pointer list-none outline-none" data-id="element-4067" data-mp="6p1ymio0o45t">
              <span className={`text-[14px] font-bold ${textPri}`} data-id="element-4068" data-mp="1flqg9k1rvic2">
                Past Transactions
              </span>
              <ChevronRightIcon className="w-5 h-5 text-gray-400 group-open:rotate-90 transition-transform" data-id="element-4069" data-mp="1aozz0te8egp2" />
            </summary>
            <div className="px-4 pb-4 pt-2 border-t border-black/5 dark:border-white/5 space-y-3" data-id="element-4070" data-mp="1oktyklt47tpi">
              <div className="flex justify-between items-center" data-id="element-4071" data-mp="1jqgaz5kp2qve">
                <div data-id="element-4072" data-mp="1yjt8s8tj3xem">
                  <p className={`text-[13px] font-semibold ${textPri}`} data-id="element-4073" data-mp="1yj9o4kchuuww">
                    PKR {formatPKR(selectedSched.amount)}
                  </p>
                  <p className={`text-[11px] ${textSec}`} data-id="element-4074" data-mp="27dxy2nt0l0s1">15/11/2026</p>
                </div>
                <span className="text-[11px] font-bold text-[#22C55E] bg-[#22C55E]/10 px-2 py-1 rounded-full" data-id="element-4075" data-mp="23bufzgbuaxxs">
                  Success
                </span>
              </div>
            </div>
          </details>

          <details className={`rounded-2xl ${cardSurface} group`} data-id="element-4076" data-mp="2ce3qw7b0stbp">
            <summary className="p-4 flex justify-between items-center cursor-pointer list-none outline-none" data-id="element-4077" data-mp="2vyu9gu2p75tk">
              <span className={`text-[14px] font-bold ${textPri}`} data-id="element-4078" data-mp="30xxdqcaut80h">
                Upcoming Transactions
              </span>
              <ChevronRightIcon className="w-5 h-5 text-gray-400 group-open:rotate-90 transition-transform" data-id="element-4079" data-mp="2lzlj0b71k9by" />
            </summary>
            <div className="px-4 pb-4 pt-2 border-t border-black/5 dark:border-white/5 space-y-3" data-id="element-4080" data-mp="2r1au1qu9kn01">
              <div className="flex justify-between items-center" data-id="element-4081" data-mp="2d68fygf9jrc5">
                <div data-id="element-4082" data-mp="2i0cnb0sfy1dr">
                  <p className={`text-[13px] font-semibold ${textPri}`} data-id="element-4083" data-mp="2i0w7yp9h73vh">
                    PKR {formatPKR(selectedSched.amount)}
                  </p>
                  <p className={`text-[11px] ${textSec}`} data-id="element-4084" data-mp="2886trmmjjbwg">
                    {selectedSched.startDate}
                  </p>
                </div>
                <span className="text-[11px] font-bold text-[#F37024] bg-[#F37024]/10 px-2 py-1 rounded-full" data-id="element-4085" data-mp="1ubbkbt2dnifv">
                  Pending
                </span>
              </div>
            </div>
          </details>
        </div>

        {selectedSched.status === 'Active' && <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] p-6 bg-gradient-to-t from-black/5 to-transparent flex gap-3" data-id="element-4086" data-mp="3w56f18ser35p">
            <button onClick={() => setShowCancelModal(true)} className={`flex-1 py-4 rounded-2xl font-bold text-[14px] border-2 border-[#EF4444] text-[#EF4444] active:scale-[0.98] transition-transform flex items-center justify-center gap-2 ${cardSurface}`} data-id="element-4087" data-mp="3m8uujvinz706">
              <Trash2Icon className="w-4 h-4" data-id="element-4088" data-mp="ds8izcyqkugt" />
              Cancel
            </button>
            {!isRecurring && <button onClick={() => {
          setSchedTab('once');
          setSchedDate(selectedSched.startDate);
          setSchedTime(selectedSched.time);
          setSchedAmount(selectedSched.amount.toString());
          setSchedPayTo(selectedSched.payTo);
          setSchedType(selectedSched.type);
          setSchedTerms(true);
          setIsEditingSched(true);
          navigateTo('scheduled_new');
        }} className="flex-1 py-4 bg-[#F37024] text-white font-bold text-[14px] rounded-2xl shadow-md active:scale-[0.98] transition-transform flex items-center justify-center gap-2" data-id="element-4089" data-mp="9q4u9mn23tdl">
                <Edit3Icon className="w-4 h-4" data-id="element-4090" data-mp="1spcg7tpy8w8m" />
                Edit
              </button>}
          </div>}

        <AnimatePresence data-id="element-4091" data-mp="1xqs5jxz5towt">
          {showCancelModal && <>
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onClick={() => setShowCancelModal(false)} className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm" data-id="element-4092" data-mp="21rbyepcpa6d5" />
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-[340px] rounded-3xl p-6 z-50 ${cardSurface}`} data-id="element-4093" data-mp="2e7bqzesb5gru">
                <div className="w-14 h-14 rounded-full bg-[#EF4444]/10 flex items-center justify-center mx-auto mb-4" data-id="element-4094" data-mp="2599f2471d3ps">
                  <AlertTriangleIcon className="w-7 h-7 text-[#EF4444]" data-id="element-4095" data-mp="20829mbsd8gma" />
                </div>
                <h3 className={`text-[18px] font-bold text-center mb-2 ${textPri}`} data-id="element-4096" data-mp="3glsehkrb78wz">
                  Cancel Schedule?
                </h3>
                <p className={`text-[13px] text-center mb-6 ${textSec}`} data-id="element-4097" data-mp="xfq2wuc001tm">
                  Are you sure you want to cancel this scheduled payment? This
                  action cannot be undone.
                </p>
                <div className="flex gap-3" data-id="element-4098" data-mp="ohntpri422v9">
                  <button onClick={() => setShowCancelModal(false)} className={`flex-1 py-3 rounded-xl font-bold text-[14px] border border-gray-300 ${textPri}`} data-id="element-4099" data-mp="tewb3pvfq2vg">
                    Keep Schedule
                  </button>
                  <button onClick={() => {
                setShowCancelModal(false);
                // Mock cancel action
                setScheduledPayments(prev => prev.filter(p => p.id !== selectedSched.id));
                navigateTo('scheduled_list');
              }} className="flex-1 py-3 bg-[#EF4444] text-white font-bold text-[14px] rounded-xl" data-id="element-4100" data-mp="1l86h2tsm79nn">
                    Cancel Schedule
                  </button>
                </div>
              </motion.div>
            </>}
        </AnimatePresence>
      </motion.div>;
  };
  const renderMerchantMenu = () => <motion.div key="merchant_menu" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-10 ${pageBg}`} style={{
    fontFamily: 'Poppins, system-ui, sans-serif'
  }} data-id="element-4101" data-mp="3klidmbnkdp6p">
      <Header title="Merchant Payment" onBackClick={() => navigateTo('services', -1)} data-id="element-4102" data-mp="2wvo2byiw88ok" />
      <div className="px-6 mt-2 space-y-4" data-id="element-4103" data-mp="31vitm36kz0a5">
        <button onClick={onNavigateToMerchantRequests} className={`w-full p-4 rounded-2xl flex items-center gap-4 text-left transition-colors ${cardSurface}`} data-id="element-4104" data-mp="31wk68c38vutz">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{
          background: isLight ? '#FBE4D3' : 'rgba(243,112,36,0.14)'
        }} data-id="element-4105" data-mp="h3gqywdfu9wh">
            <FileTextIcon className="w-5 h-5 text-[#F37024]" strokeWidth={2} data-id="element-4106" data-mp="mc7zv1ckj7ty" />
          </div>
          <div className="min-w-0 flex-1" data-id="element-4107" data-mp="hh2imfu42hc7">
            <p className={`text-[14px] font-semibold truncate ${textPri}`} data-id="element-4108" data-mp="did60pgwd3tm">
              Payment Request
            </p>
            <p className={`text-[12px] truncate ${textSec}`} data-id="element-4109" data-mp="8j9y8x9j8jhy">
              RTP Now & RTP Later
            </p>
          </div>
          <div className="bg-[#EF4444] text-white text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-4110" data-mp="3lgvyfbco9my">
            2 Pending
          </div>
          <ChevronRightIcon className={`w-5 h-5 shrink-0 ${textSec}`} strokeWidth={2} data-id="element-4111" data-mp="3qolkwn6kgozw" />
        </button>
        <button onClick={onNavigateToMerchantHistory} className={`w-full p-4 rounded-2xl flex items-center gap-4 text-left transition-colors ${cardSurface}`} data-id="element-4112" data-mp="1sxseb50wrqrk">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{
          background: isLight ? '#FBE4D3' : 'rgba(243,112,36,0.14)'
        }} data-id="element-4113" data-mp="3ieny2y5ki2i8">
            <ClockIcon className="w-5 h-5 text-[#F37024]" strokeWidth={2} data-id="element-4114" data-mp="39gm24x0oy01t" />
          </div>
          <div className="min-w-0 flex-1" data-id="element-4115" data-mp="3dgwauhfvb4kd">
            <p className={`text-[14px] font-semibold truncate ${textPri}`} data-id="element-4116" data-mp="50szo1pjqxra">
              P2M Transaction History
            </p>
            <p className={`text-[12px] truncate ${textSec}`} data-id="element-4117" data-mp="9yl41v7doccd">
              View history and request refunds
            </p>
          </div>
          <ChevronRightIcon className={`w-5 h-5 shrink-0 ${textSec}`} strokeWidth={2} data-id="element-4118" data-mp="3s7d6tl9qh29v" />
        </button>
      </div>
    </motion.div>;
  return <div className={`w-full min-h-screen overflow-hidden ${pageBg}`} data-id="element-4119" data-mp="2goix3lbbpdgi">
      <AnimatePresence mode="wait" custom={direction} data-id="element-4120" data-mp="2lpgkqdf3qzpe">
        {step === 'services' && renderServices()}
        {step === 'orgs' && renderOrgs()}
        {step === 'review' && renderReview()}
        {step === 'otp' && renderOtp()}
        {step === 'success' && renderSuccess()}
        {step === 'merchant_menu' && renderMerchantMenu()}
        {step === 'scheduled_list' && renderScheduledList()}
        {step === 'scheduled_new' && renderScheduledNew()}
        {step === 'scheduled_review' && renderScheduledReview()}
        {step === 'scheduled_mpin' && renderScheduledMpin()}
        {step === 'scheduled_success' && renderScheduledSuccess()}
        {step === 'scheduled_detail' && renderScheduledDetail()}
      </AnimatePresence>
    </div>;
}